import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useToast } from "../ui";

/**
 * Pull-to-refresh for the phone's one scroller.
 *
 * Drag down from the top of a screen and it follows the finger, a spinner
 * emerges from under the top bar into the gap, and letting go past the
 * threshold refreshes the screen: every query it is reading is fetched again,
 * and the panel eases back once they have answered. Short of the threshold it
 * eases back and nothing is fetched.
 *
 * A refresh is a refetch, not a reload. The screens draw from the query cache
 * (see `lib/queryClient`), so asking every active query again is the same
 * "is there anything new" the app's polling already asks, on demand — and it
 * keeps what was typed, where the list was scrolled to, and which tab was
 * open, none of which a page reload would.
 *
 * What it therefore cannot do is pick up a new build: the code running is the
 * code the tab was opened with, and only a reload changes that. A pull says
 * "ask the server again", not "start the app again".
 *
 * The gesture lives on the scrolling panel itself, which is what makes it
 * safe: it only arms when the panel is at its very top, and it stands down
 * the moment the finger goes back up or the panel starts to scroll, so an
 * ordinary scroll never sees it. `overscroll-behavior: contain` on the panel
 * keeps a pull at the top from chaining out to the browser, which on Android
 * would otherwise run its own reload of the whole page instead.
 *
 * What moves is the whole screen above the tab bar — the top bar with the
 * logo and the account as well as the page — not the page alone. A pull that
 * left the bar standing while the page slid out from under it read as a box
 * refreshing inside the screen; a phone app moves the screen. See
 * `PullFrame`, which is what `AppLayout` puts the bar and the page inside.
 */

/** How far the panel has to be pulled, in px, for letting go to refresh. */
export const PULL_THRESHOLD = 56;

/**
 * How far a finger has to travel before the gesture decides which way it is
 * going. 8px is the ordinary slop: short of it a touch is still settling and
 * the direction it reports is noise, past it the finger has committed.
 */
const AXIS_SLOP = 8;

/** Where the panel rests while the refresh runs — the spinner's own room. */
const HOLD_OFFSET = 56;

/** The furthest the panel will follow the finger. */
const MAX_PULL = 96;

/**
 * The least time the spinner is shown for, so a refresh that answers from
 * cache in a frame still reads as having done something — a panel that
 * snaps straight back looks like the gesture failed.
 */
const MIN_SPIN_MS = 500;

/**
 * How far the panel moves for how far the finger has. One-to-one at first
 * and ever less as it goes, so the pull has a weight to it and never runs
 * away down the screen.
 */
const pullFor = (drag: number) =>
  Math.round(MAX_PULL * (1 - Math.exp(-drag / MAX_PULL)));

/**
 * Whether the browser is certain there is no network. `onLine` is only worth
 * believing when it is false — true means "there is an interface up", which
 * is not the same as the API being reachable — and that is the one direction
 * this uses it in. Treated as online wherever it is not reported at all.
 */
const offlineNow = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Whether everything the finger came down on is scrolled to its top, from the
 * element under it up to and including the panel.
 *
 * The panel alone is not the answer. Plenty of screens fill the panel and
 * scroll *inside* themselves — the clients and enquiries lists, the detail
 * screens' bodies — and on those `panel.scrollTop` is 0 however far down the
 * reader is. Reading the panel alone armed a pull over a list that was merely
 * being scrolled back up, and then took the scroll away from it, because an
 * armed pull calls `preventDefault`.
 */
const atTop = (node: Element | null, panel: HTMLElement): boolean => {
  // The panel on its own account rather than as a step of the walk, so a
  // node that has left the DOM since the touch began — the walk then ending
  // short — is still answered against the scroller that is actually there.
  for (let at = node; at && at !== panel; at = at.parentElement)
    if (at.scrollTop > 0) return false;
  return panel.scrollTop === 0;
};

export interface PullState {
  /** How far down the panel is right now, in px; 0 at rest. */
  offset: number;
  /** True from letting go past the threshold until the queries answer. */
  refreshing: boolean;
  /** True while the finger is on the screen and the panel is following it. */
  pulling: boolean;
}

/**
 * Attaches the gesture to `panel` while `enabled`, and answers with where the
 * panel should be and whether a refresh is under way. `PageTransition` puts
 * the offset on the panel and draws `PullIndicator` in the room it opens.
 *
 * `panel` is the element itself rather than a ref, because the panel is
 * re-keyed for every screen and the listeners must follow it.
 */
export const usePullToRefresh = (
  panel: HTMLElement | null,
  enabled: boolean
): PullState => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState<PullState>({
    offset: 0,
    refreshing: false,
    pulling: false,
  });

  // Read by the listeners, which are attached once per panel and must not
  // close over a stale render.
  const refreshing = useRef(false);
  refreshing.current = state.refreshing;

  // Same reason, and it keeps the toast out of the effect's dependencies:
  // re-running that effect would detach and reattach the gesture mid-pull.
  const report = useRef(toast.error);
  report.current = toast.error;

  useEffect(() => {
    if (!panel || !enabled) return;

    // Where the finger came down, or null while the gesture is not armed.
    // Re-based mid-touch — see `onMove` — so it measures the pull rather than
    // the whole journey.
    let startY: number | null = null;
    // Where it came down, kept as it landed. This pair is what the axis is
    // read from, and it is never re-based: which way a finger is going is the
    // travel since it touched down, not since the last frame.
    let originX = 0;
    let originY = 0;
    // Which way this touch turned out to be going, decided once it has
    // travelled far enough to say and then kept for the rest of the touch.
    let axis: "none" | "pull" | "sideways" = "none";
    // What it came down on, which is what `atTop` walks up from. A touch's
    // target is fixed at `touchstart` for the life of the gesture, so this is
    // the same element every `touchmove` would report.
    let startNode: Element | null = null;
    let offset = 0;

    const standDown = () => {
      if (offset > 0) setState({ offset: 0, refreshing: false, pulling: false });
      startY = null;
      startNode = null;
      axis = "none";
      offset = 0;
    };

    const onStart = (event: TouchEvent) => {
      if (refreshing.current || event.touches.length !== 1) return;
      // Only ever from the top: a scroller being scrolled back up is not a
      // pull, however far it goes.
      startNode = event.target instanceof Element ? event.target : null;
      if (!atTop(startNode, panel)) {
        startNode = null;
        return;
      }
      startY = event.touches[0].clientY;
      originX = event.touches[0].clientX;
      originY = startY;
      axis = "none";
      offset = 0;
    };

    const onMove = (event: TouchEvent) => {
      if (startY === null) return;
      // Something under the finger has begun to scroll after all: this is a
      // scroll, and the gesture stands down until the next touch.
      if (!atTop(startNode, panel)) {
        standDown();
        return;
      }
      // Which way the finger is going, settled the first frame it has gone
      // far enough to tell and kept from then on. A row that scrolls sideways
      // under the finger — the week's highlights on the dashboard, and every
      // other carousel — sits at the top of a screen, which is exactly where
      // a pull arms, and a sideways swipe almost always drifts a pixel or two
      // down as well. Read as a pull, that drift called `preventDefault` and
      // took the swipe off the row: the cards would not move, and the page
      // followed the finger down instead. So the gesture stands down for the
      // rest of a touch that is going across, and the browser scrolls the row
      // the way it would have.
      const across = event.touches[0].clientX - originX;
      const travelled = event.touches[0].clientY - originY;
      if (
        axis === "none" &&
        (Math.abs(across) >= AXIS_SLOP || Math.abs(travelled) >= AXIS_SLOP)
      )
        axis = Math.abs(across) > Math.abs(travelled) ? "sideways" : "pull";
      if (axis === "sideways") {
        standDown();
        return;
      }
      const drag = event.touches[0].clientY - startY;
      // Not down yet. The finger is level with where it came down — which is
      // what the first move or two of a real drag very often reports, the
      // contact patch changing before the finger has travelled — or it has
      // gone back above. Either way the pull has not started, so it is
      // measured afresh from here rather than called off: reading a level
      // first move as "not a pull" left the gesture dead for the rest of the
      // touch however far it then came down, which is a pull that does
      // nothing.
      if (drag <= 0) {
        startY = event.touches[0].clientY;
        if (offset > 0) {
          offset = 0;
          setState({ offset: 0, refreshing: false, pulling: false });
        }
        return;
      }
      // Going down, but not yet far enough in any direction to say this is a
      // pull rather than the first frame of a swipe across. Nothing is
      // claimed until it is: cancelling a touch this early is what took the
      // sideways swipe off the row, since a swipe's first frame reports a
      // pixel or two of downward drift like any other.
      if (axis !== "pull") return;
      // Ours from here: the panel follows the finger and the page under it
      // is not asked to scroll as well.
      if (event.cancelable) event.preventDefault();
      offset = pullFor(drag);
      setState({ offset, refreshing: false, pulling: true });
    };

    const onEnd = () => {
      if (startY === null) return;
      startY = null;
      startNode = null;
      if (offset < PULL_THRESHOLD) {
        offset = 0;
        setState({ offset: 0, refreshing: false, pulling: false });
        return;
      }
      offset = 0;
      setState({ offset: HOLD_OFFSET, refreshing: true, pulling: false });
      const settled = new Promise((resolve) => setTimeout(resolve, MIN_SPIN_MS));

      // Every query in the cache, not only the ones this screen is reading.
      // What is on show is fetched again now; what is merely cached from a
      // screen gone by is marked stale, so walking back to it reads the
      // server rather than showing what the pull was meant to replace. The
      // polled reads carry a `staleTime` (see `lib/liveNews`) and are exactly
      // the ones that would otherwise sit unchanged for a minute after a
      // pull, which is the whole gesture doing nothing.
      const refreshed: Promise<unknown> = offlineNow()
        ? // A query the browser knows it cannot send is *paused* rather than
          // failed, and a paused refetch resolves at once — so without this
          // the spinner runs its half second over an app that asked nothing
          // and says so to nobody.
          Promise.reject(new Error("offline"))
        : // `throwOnError` because the default swallows the failure, and a
          // refresh that failed silently is the same spinner as one that
          // worked. A screen with no data of its own already draws its failed
          // state; this is for the ordinary case — figures on screen, the
          // read behind them failing — which nothing else reports.
          queryClient.invalidateQueries(undefined, { throwOnError: true });

      Promise.all([
        refreshed.then(
          () => null,
          (error: unknown) => error ?? new Error("refresh failed")
        ),
        settled,
      ]).then(([failure]) => {
        // The panel goes back either way: the gesture is over whether or not
        // the server answered.
        setState({ offset: 0, refreshing: false, pulling: false });
        if (!failure) return;
        console.error("pull to refresh failed:", failure);
        report.current(
          "Could not refresh",
          offlineNow()
            ? "You are offline. What is on screen is the last we heard."
            : "We could not reach the server. Check your connection and pull down again."
        );
      });
    };

    // Not passive, because a pull that has taken over has to stop the scroll
    // it would otherwise also be — see `onMove`.
    panel.addEventListener("touchstart", onStart, { passive: true });
    panel.addEventListener("touchmove", onMove, { passive: false });
    panel.addEventListener("touchend", onEnd);
    panel.addEventListener("touchcancel", onEnd);
    return () => {
      panel.removeEventListener("touchstart", onStart);
      panel.removeEventListener("touchmove", onMove);
      panel.removeEventListener("touchend", onEnd);
      panel.removeEventListener("touchcancel", onEnd);
    };
  }, [panel, enabled, queryClient]);

  return state;
};

/**
 * The spinner in the room a pull opens above the panel.
 *
 * Drawn at the top of the stage, under the panel in the stacking order, and
 * moved down with the pull so it comes out from under the top bar as the gap
 * grows: it is at rest when the panel is, and fully in view just as the pull
 * reaches the threshold. It turns with the pull on the way down — a hint
 * that letting go does something — and spins on its own once let go.
 */
export const PullIndicator: React.FC<PullState> = ({
  offset,
  refreshing,
  pulling,
}) => {
  const progress = Math.min(1, offset / PULL_THRESHOLD);
  // Announced only once it is doing something: a spinner following a finger
  // is not news to the person whose finger it is.
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={refreshing ? "Refreshing" : undefined}
      className="pointer-events-none absolute left-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand shadow-[0_2px_10px_rgba(30,31,36,0.16)]"
      style={{
        // From under the status bar rather than the screen's edge, so on a
        // notched phone it is not drawn behind the clock.
        top: "env(safe-area-inset-top, 0px)",
        // The circle's own height plus a little air, so it is wholly above
        // the screen's top edge whatever the offset.
        transform: `translate(-50%, ${offset - 44}px)`,
        opacity: progress,
        // Snaps to the finger while pulling and eases everywhere else — back
        // up after a short pull, and back out once the refresh has landed.
        transition: pulling
          ? "none"
          : "transform 200ms ease-out, opacity 200ms ease-out",
      }}
    >
      <Loader2
        className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`}
        style={refreshing ? undefined : { transform: `rotate(${progress * 180}deg)` }}
        aria-hidden="true"
      />
    </div>
  );
};

interface PullFrameProps {
  /** The scrolling panel the gesture is read from — see `usePullToRefresh`. */
  panel: HTMLElement | null;
  /** Off from `md` up, where there is no finger to pull. */
  enabled: boolean;
  /** The top bar and the page: everything above the tab bar. */
  children: React.ReactNode;
}

/**
 * What a pull moves. Wraps the top bar and the page in one box that follows
 * the finger, with `PullIndicator` drawn behind it in the room it opens. The
 * parent must be `relative` for the indicator to sit against its top.
 *
 * The box is laid out exactly as its children were on their own — a column
 * taking the height left above the tab bar — so wrapping them here changes
 * nothing about the screen at rest.
 */
export const PullFrame: React.FC<PullFrameProps> = ({
  panel,
  enabled,
  children,
}) => {
  const pull = usePullToRefresh(panel, enabled);
  return (
    <>
      {enabled && <PullIndicator {...pull} />}
      <div
        className="flex-1 min-h-0 flex flex-col"
        style={{
          // Following the finger, or held open while a refresh runs. Cleared
          // at rest, so there is no transform on the box then.
          transform: pull.offset ? `translateY(${pull.offset}px)` : undefined,
          // Snaps to the finger while pulling; eases back otherwise.
          transition: pull.pulling ? "none" : "transform 200ms ease-out",
        }}
      >
        {children}
      </div>
    </>
  );
};
